import {
  FinancialClass,
  InvestmentFinancialMetricsDTO,
  MetricValueDTO,
  TransactionType,
} from '@inversor-hub/types';
import { formatPercentage } from '@/utils/misc';

// The API answers in English and the product is Spanish-only, so never surface
// `reason` or `notes` verbatim: everything user-facing is rebuilt from the
// structured fields of the response.

export const financialClassLabels: Record<FinancialClass, string> = {
  OPERATING_INCOME: 'Ingreso del alquiler',
  OPERATING_EXPENSE: 'Gasto operativo',
  INTEREST: 'Intereses de la hipoteca',
  PRINCIPAL: 'Amortización de principal',
  CAPITAL_EXPENDITURE: 'Inversión / mejora (capex)',
  OTHER: 'Otros movimientos',
  NON_CASH: 'Sin movimiento de caja',
  UNCLASSIFIED: 'Sin clasificar',
};

export const financialClassHelp: Record<FinancialClass, string> = {
  OPERATING_INCOME: 'Suma al beneficio del alquiler: rentas y gastos repercutidos al inquilino.',
  OPERATING_EXPENSE:
    'Resta del beneficio del alquiler: IBI, comunidad, seguro, suministros, gestión…',
  INTEREST: 'Parte de intereses de la cuota. No afecta al beneficio del alquiler.',
  PRINCIPAL: 'Parte de capital de la cuota. No afecta al beneficio del alquiler.',
  CAPITAL_EXPENDITURE: 'Reformas y mejoras que añaden valor al activo.',
  OTHER: 'Venta, fianzas y movimientos fuera de la explotación.',
  NON_CASH: 'Amortizaciones contables e impagos: no hay dinero moviéndose.',
  UNCLASSIFIED: 'Queda fuera de todos los cálculos hasta que lo clasifiques.',
};

const classesByTransactionType: Record<TransactionType, FinancialClass[]> = {
  INCOME: ['OPERATING_INCOME', 'OTHER', 'NON_CASH'],
  EXPENSE: [
    'OPERATING_EXPENSE',
    'INTEREST',
    'PRINCIPAL',
    'CAPITAL_EXPENDITURE',
    'OTHER',
    'NON_CASH',
  ],
};

// The API rejects a class that contradicts the transaction type with a 400, so
// only offer the ones it will accept.
export const allowedFinancialClasses = (type: TransactionType): FinancialClass[] =>
  classesByTransactionType[type];

// `formatPercentage` returns '-' for any falsy input, so a legitimate 0 % would
// disappear. Rates go through here instead.
export const formatRate = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value === 0 ? '0,00 %' : formatPercentage(value);

export const formatRatio = (metric: MetricValueDTO): string => formatRate(metric.value);

export const unavailableHint = (
  key: 'returnOnMarketValue' | 'yieldOnCost',
  metrics: InvestmentFinancialMetricsDTO
): string => {
  if (key === 'returnOnMarketValue') {
    return metrics.valuation
      ? 'La valoración registrada es de 0 €.'
      : 'Añade una valoración de mercado para calcular esta rentabilidad.';
  }
  return metrics.initialCost.amount === 0
    ? 'No hay ningún coste de compra registrado en la ficha de la inversión.'
    : 'Falta el coste inicial de la inversión.';
};

export const unclassifiedHint = (count: number): string =>
  `${count} ${count === 1 ? 'movimiento está' : 'movimientos están'} sin clasificar y ${count === 1 ? 'queda' : 'quedan'} fuera de este número.`;

export const ANNUAL_ESTIMATE_HELP =
  'Previsión anual según la ficha: usa el alquiler esperado vigente, los gastos previstos guardados y las próximas doce cuotas del cuadro hipotecario. No se calcula a partir de las transacciones reales.';

export const ACTUAL_ANNUALIZED_HELP =
  'Ritmo anualizado YTD: extrapola linealmente a un año los cobros y pagos registrados hasta la fecha. Sirve para comparar, pero puede verse afectado por estacionalidad o movimientos puntuales.';

export const incompleteCostHint =
  'El coste inicial no está marcado como completo, así que este porcentaje es provisional.';

export const buildQualityNotes = (metrics: InvestmentFinancialMetricsDTO): string[] => {
  const notes = [
    'Los importes son de caja: cuentan los cobros y pagos con fecha dentro del periodo, no lo devengado.',
  ];

  if (metrics.period.isYearToDate) {
    notes.push(`Los importes reales acumulados llegan hasta el ${metrics.period.endDate}.`);
  }
  if (metrics.actualAnnualized) {
    notes.push(
      'El ritmo anualizado YTD extrapola linealmente los movimientos registrados. Es una referencia comparable, no un resultado ya realizado, y puede distorsionarse por estacionalidad o pagos puntuales.'
    );
  }
  if (metrics.annualEstimate) {
    notes.push(
      'La previsión de la ficha usa el alquiler esperado vigente, los gastos previstos guardados y las próximas doce cuotas del cuadro hipotecario. No se calcula a partir de los cobros y pagos YTD.'
    );
  }
  notes.push(
    'El ROE total es acumulado, no anualizado. La revalorización resta el capex registrado para no tratar una aportación de capital como rentabilidad.'
  );
  if (metrics.equityMetrics.totalRoe.value === null) {
    notes.push(
      'Para calcular el retorno sobre equity hacen falta un equity inicial y movimientos de caja o principal. La revalorización se añade cuando existen valoraciones próximas al inicio y al final del periodo.'
    );
  } else if (metrics.equityMetrics.appreciation.value === null) {
    notes.push(
      'El retorno sobre equity incluye caja y principal amortizado, pero no revalorización porque falta una valoración válida al cierre.'
    );
  }
  if (metrics.quality.unclassifiedLineCount > 0) {
    notes.push(
      `${metrics.quality.unclassifiedLineCount} movimientos sin clasificar no entran en ninguna métrica.`
    );
  }
  if (!metrics.initialCost.complete) {
    notes.push(
      'El coste inicial no está confirmado como completo, así que la rentabilidad sobre lo invertido es provisional.'
    );
  }
  if (!metrics.valuation) {
    notes.push(
      'Sin valoración de mercado no se puede calcular la rentabilidad sobre valor actual.'
    );
  }

  return notes;
};
